import { APP_LINKS } from "@/lib/constants";

// Official store badges, self-hosted (public/images/badges/) so they look the
// same everywhere: Google Play's "Get it on" PNG as published, and Apple's
// black "Download on the App Store" SVG with its black background filled
// explicitly (Apple's file leaves it as the default fill, which dark-mode
// browsers re-colour grey). Otherwise unmodified artwork.

interface AppStoreBadgesProps {
  placement: string;
  size?: "sm" | "md" | "lg";
  /** "start" lines the badges up with left-aligned text (footer). */
  align?: "center" | "start";
  className?: string;
  /** Show only this store (e.g. the one for the visitor's phone). Default: both. */
  only?: "googlePlay" | "appStore";
  /** Called when a badge is clicked (analytics); the link opens as usual. */
  onBadgeClick?: (store: "googlePlay" | "appStore") => void;
}

export function AppStoreBadges({
  placement,
  size = "md",
  align = "center",
  className = "",
  only,
  onBadgeClick,
}: AppStoreBadgesProps) {
  const isLarge = size === "lg";

  return (
    <div
      className={`flex items-center ${align === "start" ? "justify-start" : "justify-center"} ${isLarge ? "flex-col gap-2 sm:flex-row sm:gap-5" : "flex-wrap gap-3"} ${className}`}
    >
      {only !== "appStore" && (
        <a
          href={`${APP_LINKS.googlePlay}&utm_source=website&utm_medium=${placement}&utm_campaign=download`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onBadgeClick ? () => onBadgeClick("googlePlay") : undefined}
          className={`inline-block transition-opacity hover:opacity-80 ${isLarge ? "w-[260px] sm:w-auto" : ""}`}
          style={{ transitionDuration: "var(--transition-fast)" }}
        >
          {/* Google Play badge PNG has ~15% built-in transparent padding */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/badges/google-play-badge.png"
            alt="Get it on Google Play"
            width={isLarge ? 280 : size === "md" ? 200 : 160}
            height={isLarge ? 84 : size === "md" ? 60 : 48}
            className={isLarge ? "w-full sm:w-[280px]" : ""}
            loading="lazy"
          />
        </a>
      )}
      {only !== "googlePlay" && (
        <a
          href={`${APP_LINKS.appStore}?utm_source=website&utm_medium=${placement}&utm_campaign=download`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onBadgeClick ? () => onBadgeClick("appStore") : undefined}
          className={`inline-block transition-opacity hover:opacity-80 ${isLarge ? "w-[235px] sm:w-auto" : ""}`}
          style={{ transitionDuration: "var(--transition-fast)" }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/badges/app-store-badge.png"
            alt="Download on the App Store"
            width={isLarge ? 230 : size === "md" ? 165 : 130}
            height={isLarge ? 70 : size === "md" ? 50 : 40}
            className={isLarge ? "w-full sm:w-[230px]" : ""}
            loading="lazy"
          />
        </a>
      )}
    </div>
  );
}
